/**
 * Поля операции карточкой — по макету «Корректировка операции» (Figma 23:880,
 * 63:625).
 *
 * Счёт, категория и дата — строки одной белой карточки, разделённые линией;
 * категория — цветная плашка, как у позиции чека; заметка — своё поле под
 * карточкой; пояснение — значок «i» у заголовка листа (`SheetWrap tip`).
 * Выбор внутри — те же `AccountField` и `CategoryField`, что у ручной записи:
 * меняется только облик строки, а не то, как выбирают.
 *
 * Стили — от разобранного чека (`.sheet.rcpt`): лист, где стоят эти поля,
 * получает класс `rcpt`.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Account } from '@skoka/core';
import { AccountGlyph, IconGlyph } from './account-glyph.js';
import { bankOf } from './banks.js';
import { ColorRow, IconPicker } from './icon-field.js';
import { ACCOUNT_ICON_CHOICES, PACK_ICONS } from './icon-pack.js';
import { SheetWrap } from './sheet.js';
import { AmountInput } from './amount.js';
import { Tip } from './tip.js';
import type { Category } from './categories.js';
import type { CategoryKind } from './category-tree.js';
import { AccountField, CategoryField, ChoiceSheet, accountBalanceText, type ChoiceOption } from './operation-fields.js';
import { CategoryGlyph, colorOf, type CategoryColor } from './category-icon.js';
import { Icon } from './icon.js';
import { DateButton } from './date-picker.js';

const Chevron = () => <Icon name="chevron-right" size={14} className="rcchev" />;

/** Строка «Счёт»: значок счёта, название и остаток под ним. */
export function CardAccount({ label, pickerTitle, accounts, value, onChange }: {
  label: string;
  pickerTitle?: string;
  accounts: Account[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <AccountField label={label} {...(pickerTitle ? { pickerTitle } : {})} accounts={accounts} value={value} onChange={onChange}
      trigger={(open, current) => (
        <button className="rcselrow" onClick={open} aria-label={`${label}: ${current?.name ?? 'Выбрать'}`}>
          <span className="rclabel">{label}</span>
          <span className="rcvalue">
            <span className="rcvalcol">
              <span className="rcvalline">
                {/* Значок того счёта, который человек выбрал для него сам. */}
                {current && <AccountGlyph account={current} size="f" />}
                <span className="rcvaltext">{current?.name ?? 'Выбрать счёт'}</span>
              </span>
              {current && <small>{accountBalanceText(current)}</small>}
            </span>
            <Chevron />
          </span>
        </button>
      )} />
  );
}

/** Строка «Категория»: плашка цвета категории со значком. */
export function CardCategory({ categories, kind, value, onChange, onUpdated, unsure = false, label = 'Категория' }: {
  categories: Category[];
  kind: CategoryKind;
  value: string;
  onChange: (id: string) => void;
  onUpdated: (categories: Category[]) => void;
  /** Категорию подобрала модель, и её стоит проверить: плашка обведена. */
  unsure?: boolean;
  /** Подпись строки: у возврата это «За что вернули». */
  label?: string;
}) {
  return (
    <CategoryField categories={categories} kind={kind} value={value} onChange={onChange} onUpdated={onUpdated}
      trigger={(open, current) => (
        <button className="rcselrow rccatrow" onClick={open} aria-label={`${label}: ${current?.name ?? 'не выбрана'}`}>
          <span className="rclabel">{label}</span>
          <span className={`rcpill c-${colorOf(current)}${unsure ? ' guess' : ''}`}>
            {current && <CategoryGlyph icon={current.icon} color={colorOf(current)} size="xs" />}
            <span className="rcpilltext">{current?.name ?? 'Выбрать'}</span>
            <Chevron />
          </span>
        </button>
      )} />
  );
}

/**
 * Строка выбора из списка.
 *
 * Тап по строке — варианты строками в листе снизу, как у `PickRow`. У варианта
 * может быть пояснение мелкой строкой (`sub`): оно видно и в листе, и под
 * выбранным значением в строке (22.10.2026).
 */
export function CardPick({ label, tip, value, options, onChange, sub, empty = '—', warn = false, title, lead, sheetTip }: {
  label: string;
  tip?: ReactNode;
  value: string;
  /** Вариант со значком (`icon`, `tone`) — значок и у значения в строке. */
  options: ChoiceOption[];
  /** Вопрос над вариантами в листе выбора. */
  lead?: ReactNode;
  /** Пояснение (i) у заголовка листа выбора. */
  sheetTip?: ReactNode;
  onChange: (id: string) => void;
  /** Строка под значением; без неё — пояснение выбранного варианта, если оно короткое. */
  sub?: string;
  /** Что написано, пока ничего не выбрано. */
  empty?: string;
  /** Значение надо выбрать, а оно не выбрано: строка подсвечена. */
  warn?: boolean;
  /** Заголовок листа выбора, если он не совпадает с подписью строки. */
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <div className={`rcselrow${warn && !current ? ' rcwarn' : ''}`}>
      <RowLabel label={label} {...(tip ? { tip } : {})} />
      <span className="rcvalue">
        <span className="rcvalcol">
          {current?.icon
            ? <span className="rcvalline">
              <span className="rcvalico" style={{ color: current.tone }}><Icon name={current.icon} size={14} /></span>
              <span className="rcvaltext">{current.title}</span>
            </span>
            : <span className={`rcvaltext${current ? '' : ' rcnone'}`}>{current?.title ?? empty}</span>}
          {sub && <small>{sub}</small>}
        </span>
        <Chevron />
      </span>
      <button type="button" className="opover overbtn" aria-label={`${label}: ${current?.title ?? 'не выбрано'}`} onClick={() => setOpen(true)} />
      {open && <ChoiceSheet title={title ?? label} value={value} options={options} {...(lead ? { lead } : {})} {...(sheetTip ? { tip: sheetTip } : {})} onPick={onChange} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** Строка «Когда»: «Сегодня, 19:14»; тап открывает выбор даты и времени. */
export function CardWhen({ text, date, time, max, onChange }: {
  text: string;
  date: string;
  time: string;
  /** Позже этого выбрать нельзя: операция уже случилась. */
  max: string;
  onChange: (date: string, time: string) => void;
}) {
  return (
    <div className="rcselrow">
      <span className="rclabel">Когда</span>
      <span className="rcvalue">
        <span className="rcvaltext">{text}</span>
        <Chevron />
      </span>
      {/* Тап по строке — свой календарь листом, со временем под ним. */}
      <DateButton overlay label="Дата и время операции" value={date} max={max.slice(0, 10)}
        time={time} onTime={(t) => onChange(date, t)} onChange={(d) => onChange(d, time)} />
    </div>
  );
}

/**
 * Заметка — поле под карточкой. Пустое — с карандашом и «Добавить заметку…»,
 * написанное — просто текст, который растёт вместе со строками.
 */
export function NoteField({ value, onChange }: { value: string; onChange: (note: string) => void }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <label className="rcnotefield">
      {!value && <Icon name="pen" size={15} />}
      <textarea ref={ref} rows={1} maxLength={1000} value={value} aria-label="Заметка"
        placeholder="Добавить заметку…" onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/** Подпись строки с необязательным «i». */
const RowLabel = ({ label, tip }: { label: string; tip?: ReactNode }) => (
  <span className="rclabel">{label}{tip ? <Tip>{tip}</Tip> : null}</span>
);

/** Строка с полем справа: название, ставка, число платежей. Ввод — прямо в строке. */
export function CardInput({ label, tip, value, onChange, unit, placeholder, inputMode, width }: {
  label: string;
  tip?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  /** Подпись после числа: «% годовых». */
  unit?: string;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal';
  /** Ширина поля, если значение короткое: ставка, число платежей. */
  width?: number;
}) {
  return (
    <label className="rcselrow rcinputrow">
      <RowLabel label={label} {...(tip ? { tip } : {})} />
      <span className="rcvalue">
        <input className="rcinline" value={value} aria-label={label}
          {...(placeholder ? { placeholder } : {})} {...(inputMode ? { inputMode } : {})}
          {...(width ? { style: { width } } : {})}
          // Нажал на строку — значение выделено целиком, и набранное заменяет
          // прежнее (22.10.2026: курсор вставал в начало, и ставку «12»
          // приходилось стирать руками). Выделяем после фокуса: Safari
          // сбрасывает выделение, сделанное в самом обработчике.
          onFocus={(e) => { const el = e.currentTarget; requestAnimationFrame(() => el.setSelectionRange(0, el.value.length)); }}
          onChange={(e) => onChange(e.target.value)} />
        {unit && <span className="rcunit">{unit}</span>}
      </span>
    </label>
  );
}

/** Строка суммы: ввод с разрядами, как сумма операции, и знак рубля. */
export function CardMoney({ label, tip, value, onChange, placeholder = '0' }: {
  label: string;
  tip?: ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="rcselrow rcinputrow">
      <RowLabel label={label} {...(tip ? { tip } : {})} />
      <span className="rcvalue">
        <AmountInput value={value} onChange={onChange} placeholder={placeholder} label={label} className="rcinline" />
        <span className="rcunit">₽</span>
      </span>
    </label>
  );
}

/**
 * Строка даты: дата словами, тап открывает свой календарь листом.
 * Пустое значение — «не указана» бледным.
 */
export function CardDate({ label, tip, value, text, sub, min, max, clear, onChange }: {
  label: string;
  tip?: ReactNode;
  value: string;
  /** Дата словами; пустое значение — текст-заглушка. */
  text: string;
  /** Строка под датой: «дальше каждый месяц». */
  sub?: string;
  min?: string;
  max?: string;
  /** Дата необязательна: как остаться без неё — «Не указана». */
  clear?: string;
  onChange: (date: string) => void;
}) {
  return (
    <div className="rcselrow">
      <RowLabel label={label} {...(tip ? { tip } : {})} />
      <span className="rcvalue">
        <span className="rcvalcol">
          <span className={`rcvaltext${value ? '' : ' rcnone'}`}>{text}</span>
          {sub && <small>{sub}</small>}
        </span>
        <Chevron />
      </span>
      <DateButton overlay label={label} value={value} min={min} max={max} {...(clear ? { clear } : {})}
        onChange={(d) => onChange(clampDate(d, min, max))} />
    </div>
  );
}

/**
 * Дата в пределах поля.
 *
 * Колесо выбора даты на iPhone не соблюдает `min` и `max`: досрочный платёж
 * записывался 20 октября, когда «Когда» разрешало не позже сегодняшнего
 * 15-го. Деньги со счёта уходили сразу, а долг уменьшался только через пять
 * дней. Поэтому граница держится здесь, а не в браузере.
 */
export const clampDate = (v: string, min?: string, max?: string): string =>
  !v ? v : max && v > max ? max : min && v < min ? min : v;

/**
 * Строка-переключатель: «Можно гасить досрочно», «Возвращаю частями».
 *
 * `sub` — что переключатель меняет в расчёте, мелкой строкой под подписью:
 * галочка без пояснения не говорила, зачем она (22.10.2026).
 */
export function CardToggle({ label, tip, sub, checked, onChange }: {
  label: string;
  tip?: ReactNode;
  sub?: ReactNode;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <label className={`rcselrow rctogglerow${sub ? ' rcsubrow' : ''}`}>
      {sub
        ? <span className="rclabel"><span>{label}{tip ? <Tip>{tip}</Tip> : null}</span><small>{sub}</small></span>
        : <RowLabel label={label} {...(tip ? { tip } : {})} />}
      <input type="checkbox" role="switch" className="rcswitch" checked={checked} aria-label={label}
        onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

/**
 * Главная сумма листа — крупно, справа у «₽».
 *
 * Число растёт влево, а знак рубля стоит на месте: поле не прыгает, как ни
 * длинна сумма (замечание владельца 22.10.2026).
 */
export function BigAmount({ label, tip, hint, value, onChange, placeholder = '0', readOnly, right }: {
  label: string;
  tip?: ReactNode;
  /** Мелкая подпись справа над полем: «можно не знать». */
  hint?: string;
  value?: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  /** Сумма не правится здесь: долг по карте меняют корректировкой. */
  readOnly?: string;
  /** Своё справа над полем: «на сегодня», «сверен 20 окт.». */
  right?: ReactNode;
}) {
  return (
    <>
      <div className="rcamounthead">
        <span>{label}</span>{tip ? <Tip>{tip}</Tip> : null}
        <span className="grow" />
        {hint && <span className="rchint">{hint}</span>}
        {right}
      </div>
      {readOnly !== undefined ? (
        <div className="rcamountedit readonly"><span className="num">{readOnly}</span></div>
      ) : (
        <label className="rcamountedit">
          <AmountInput value={value ?? ''} onChange={onChange ?? (() => {})} placeholder={placeholder} label={label} className="num" />
          <span aria-hidden="true">₽</span>
        </label>
      )}
    </>
  );
}

/** Вид записи — переключатель сверху листа, как у кредита. */
export function Segs<T extends string>({ label, options, value, onChange }: {
  label: string;
  options: ReadonlyArray<readonly [T, string]>;
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="segs" role="group" aria-label={label}>
      {options.map(([id, title]) => (
        <button key={id} type="button" className={`seg${value === id ? ' on' : ''}`} aria-pressed={value === id}
          onClick={() => onChange(id)}>{title}</button>
      ))}
    </div>
  );
}

/** «Ещё» / «Ещё условия»: редкое свёрнуто под кнопкой. */
export function MoreButton({ label, open, onToggle }: { label: string; open: boolean; onToggle: () => void }) {
  return (
    <button type="button" className={`rcview more${open ? ' on' : ''}`} aria-expanded={open} onClick={onToggle}>
      {label} <Icon name="chevron-down" size={12} />
    </button>
  );
}

/** Подзаголовок группы строк: «Как копим», «По выписке». */
export const CardHead = ({ children, tip }: { children: ReactNode; tip?: ReactNode }) => (
  <div className="rchead">{children}{tip ? <Tip>{tip}</Tip> : null}</div>
);

/** «10-го», «10 и 25», «5, 15 и 25». */
export const daysText = (days: readonly number[]): string => {
  if (days.length === 0) return '';
  if (days.length === 1) return `${days[0]}-го`;
  return `${days.slice(0, -1).join(', ')} и ${days[days.length - 1]}`;
};

/**
 * Числа месяца нажатием.
 *
 * Прежде их вписывали словами — «10 или 10 и 25», — и непонятное «5,20»
 * молча становилось одним числом. Здесь сетка 1–31: выбрал — видно.
 */
export function CardDays({ label, tip, value, onChange, max = 1, sub, note, empty }: {
  label: string;
  tip?: ReactNode;
  value: readonly number[];
  onChange: (days: number[]) => void;
  /** Сколько чисел можно выбрать: у зарплаты с авансом — два. */
  max?: number;
  sub?: string;
  /** Пояснение под сеткой. */
  note?: string;
  /** Число необязательно: что написано без него («если знаешь»), и как его снять. */
  empty?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState<number[]>([...value]);
  const text = value.length > 0 ? daysText(value) : empty ?? 'Выбрать';
  const toggle = (d: number) => {
    if (max === 1) { onChange([d]); setOpen(false); return; }
    setPick((p) => {
      if (p.includes(d)) return p.filter((x) => x !== d);
      // Сверх предела уходит самое раннее из выбранных: третье нажатие
      // меняет одно число, а не молчит.
      const next = [...p, d];
      return (next.length > max ? next.slice(next.length - max) : next).sort((a, b) => a - b);
    });
  };
  return (
    <div className={`rcselrow${value.length === 0 && empty === undefined ? ' rcwarn' : ''}`}>
      <RowLabel label={label} {...(tip ? { tip } : {})} />
      <span className="rcvalue">
        <span className="rcvalcol">
          <span className={`rcvaltext${value.length > 0 ? '' : ' rcnone'}`}>{text}</span>
          {sub && <small>{sub}</small>}
        </span>
        <Chevron />
      </span>
      <button type="button" className="opover overbtn" aria-label={`${label}: ${text}`}
        onClick={() => { setPick([...value]); setOpen(true); }} />
      {open && (
        <SheetWrap title={label} onClose={() => setOpen(false)}>
          <div className="daygrid" role="group" aria-label={label}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
              const on = (max === 1 ? value : pick).includes(d);
              return (
                <button key={d} type="button" className={on ? 'on' : ''} aria-pressed={on} onClick={() => toggle(d)}>{d}</button>
              );
            })}
          </div>
          {note && <p className="sheetnote">{note}</p>}
          {empty !== undefined && value.length > 0 && (
            <button type="button" className="textact" onClick={() => { onChange([]); setOpen(false); }}>Не знаю</button>
          )}
          {max > 1 && (
            <button type="button" className="btn pri" disabled={pick.length === 0}
              onClick={() => { onChange(pick); setOpen(false); }}>Готово</button>
          )}
        </SheetWrap>
      )}
    </div>
  );
}

/** Подпись значка: банк по имени, значок счёта — его словом. */
const lookName = (icon: string): string =>
  bankOf(icon)?.name ?? ACCOUNT_ICON_CHOICES.find((c) => c.name === icon)?.label
    ?? PACK_ICONS.find((c) => c.name === icon)?.label ?? 'Значок';

/**
 * «Значок и цвет» — одной строкой; выбор — в своём листе.
 *
 * Прежде это были два поля, «Иконка» и «Цвет», посреди формы. У логотипа
 * банка цвета свои — строки цвета в листе нет.
 */
export function CardLook({ icon, color, onChange, forAccount = true }: {
  icon: string;
  color: CategoryColor;
  /** Значки счёта (банки, деньги) или категории. */
  forAccount?: boolean;
  onChange: (icon: string, color: CategoryColor) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rcselrow">
      <span className="rclabel">Значок и цвет</span>
      <span className="rcvalue">
        <span className="rcvalline">
          <IconGlyph icon={icon} color={color} size="f" />
          <span className="rcvaltext">{lookName(icon)}</span>
        </span>
        <Chevron />
      </span>
      <button type="button" className="opover overbtn" aria-label={`Значок и цвет: ${lookName(icon)}`} onClick={() => setOpen(true)} />
      {open && (
        <SheetWrap title="Значок и цвет" onClose={() => setOpen(false)}
          action={<button type="button" className="textact inline" onClick={() => setOpen(false)}>Готово</button>}>
          {!bankOf(icon) && <ColorRow value={color} onChange={(c) => onChange(icon, c)} />}
          <IconPicker value={icon} color={color} forAccount={forAccount} onPick={(i) => onChange(i, color)} />
        </SheetWrap>
      )}
    </div>
  );
}
